import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

const DEFAULT_DELAY_DURATION = 700;
const DEFAULT_SKIP_DELAY_DURATION = 300;

export type TooltipProviderContextType = {
  delayDuration: number;
  isWithinSkipDelay: () => boolean;
  notifyClose: () => void;
  notifyOpen: () => void;
};

export const TooltipProviderContext =
  createContext<TooltipProviderContextType | null>(null);

export type TooltipProviderProps = {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
};

export const TooltipProvider = (props: TooltipProviderProps) => {
  const {
    children,
    delayDuration = DEFAULT_DELAY_DURATION,
    skipDelayDuration = DEFAULT_SKIP_DELAY_DURATION,
  } = props;

  const lastClosedAtRef = useRef(0);
  const openCountRef = useRef(0);
  const skipDelayRef = useRef(skipDelayDuration);
  skipDelayRef.current = skipDelayDuration;

  const notifyOpen = useCallback(() => {
    openCountRef.current += 1;
  }, []);

  const notifyClose = useCallback(() => {
    openCountRef.current = Math.max(0, openCountRef.current - 1);
    if (openCountRef.current === 0) {
      lastClosedAtRef.current = Date.now();
    }
  }, []);

  const isWithinSkipDelay = useCallback(() => {
    if (skipDelayRef.current <= 0) return false;
    if (openCountRef.current > 0) return true;
    return Date.now() - lastClosedAtRef.current < skipDelayRef.current;
  }, []);

  const contextValue = useMemo(
    () => ({ delayDuration, isWithinSkipDelay, notifyClose, notifyOpen }),
    [delayDuration, isWithinSkipDelay, notifyClose, notifyOpen],
  );

  return (
    <TooltipProviderContext.Provider value={contextValue}>
      {children}
    </TooltipProviderContext.Provider>
  );
};

export const useTooltipProvider = (): TooltipProviderContextType | null =>
  useContext(TooltipProviderContext);
